/**
 * Sim events → presentation (doc 17 §4, §7). One switch, one place: when a
 * sound, a shake, a toast or a number is wrong, it is wrong on one line here.
 *
 * The run screen owns the state; this owns the mapping. Everything it needs is
 * on `EventHost`, and that list is deliberately short enough to read.
 */

import { L, S } from '../content/strings.ts';
import { audio } from '../audio/engine.ts';
import { blockCentre, TH, TW } from '../render/iso.ts';
import { PLACEABLE, placeableDef } from '../sim/defs.ts';
import type { Sim } from '../sim/sim.ts';
import { SPECIES } from '../sim/species.ts';
import type { RimShoal, SimEvent } from '../sim/types.ts';
import type { Alerts } from './alerts.ts';
import type { DamageNumbers } from './damagenumbers.ts';
import type { EffectsView } from './effects.ts';
import { BOSS_HIT_STOP_DAMAGE, type Juice } from './juice.ts';
import type { RunLedger } from './ledger.ts';
import type { WorldView } from './worldview.ts';

export interface EventHost {
  readonly sim: Sim;
  readonly fx: EffectsView;
  readonly world: WorldView;
  readonly ledger: RunLedger;
  readonly juice: Juice;
  readonly numbers: DamageNumbers;
  readonly alerts: Alerts;
  say(text: string, ms?: number): void;
  showBanner(title: string, sub: string, hold?: boolean): void;
  introduceProperty(shoal: RimShoal): void;
  bankClear(ev: Extract<SimEvent, { type: 'waveCleared' }>): void;
  finish(outcome: 'won' | 'lost'): void;
}

/** Doc 17 §7: a mortar landing shakes 1 px and freezes 40 ms. Nothing else does. */
export function handleEvents(h: EventHost, events: SimEvent[], now: number): void {
  const sim = h.sim;
  for (const ev of events) {
    switch (ev.type) {
      case 'shot':
        h.fx.tracer(
          Math.round((ev.x - ev.y) * TW),
          Math.round((ev.x + ev.y) * TH + TH) - 16,
          Math.round((ev.hitX - ev.hitY) * TW),
          Math.round((ev.hitX + ev.hitY) * TH + TH) - 6,
        );
        break;
      case 'chain':
        h.fx.chain(ev.points);
        break;
      case 'launch':
        h.fx.shell(ev.fromX, ev.fromY, ev.toX, ev.toY, ev.landTick - sim.state.tick);
        break;
      case 'explosion':
        h.fx.explosion(ev.x, ev.y, ev.radius);
        h.juice.shake('mortar', now);
        h.juice.freeze(now);
        break;
      case 'enemyHit': {
        h.numbers.add(ev.enemyId, ev.x, ev.y, ev.dmg, ev.crit, now);
        // stage 7a: the fish that took it flashes. Doc 10's damaged row has
        // always said so; before the bestiary graduated there was no state to
        // put it in, so a shot landing looked exactly like a shot missing.
        h.world.noteHit(ev.enemyId);
        // doc 17 §7: a big hit on a boss stops the world for 40 ms. A big hit
        // on a krill does not, because a krill does not survive to notice.
        const e = sim.state.enemies.find((x) => x.id === ev.enemyId);
        if (ev.dmg >= BOSS_HIT_STOP_DAMAGE && e && SPECIES[e.species]?.boss) {
          h.juice.freeze(now);
          h.juice.shake('boss', now);
        }
        break;
      }
      case 'enemyDied':
        h.fx.puff(ev.x, ev.y);
        if (SPECIES[ev.species]?.boss) {
          h.fx.flare(ev.x, ev.y, h.juice.flash);
          h.juice.shake('boss', now);
        }
        break;
      case 'grab': {
        const t = sim.byId(ev.towerId);
        if (t) {
          const c = blockCentre(t.i, t.j, t.size, t.size);
          h.fx.grabFlash(c.x, c.y - 8);
          h.alerts.raise(t.id, L.bossHasTower(ev.bossName), 'loud', { x: t.i, y: t.j }, now);
        }
        h.say(L.bossHugging(ev.bossName), 3000);
        break;
      }
      case 'act2':
        h.showBanner(S.ev.act, S.ev.newShoalsJoin);
        break;
      case 'cast':
        h.say(S.ev.volleyAwayBudget, 2000);
        break;
      case 'pulled': {
        const shoal = sim.state.rim.find((x) => x.name === ev.name);
        const stars = shoal ? '*'.repeat(shoal.menace) : '';
        const extra = ev.doubleReel ? ` · DOUBLE REEL +${Math.round(sim.mods.doubleReelBonus * 100)}%` : '';
        h.showBanner(S.ev.reeling, `${ev.name} ${stars}${extra}`);
        if (shoal) h.ledger.note(shoal.id);
        const wave = sim.state.waves.at(-1);
        if (wave?.riled) h.world.markRiled(wave.id);
        if (shoal) h.introduceProperty(shoal);
        break;
      }
      case 'spit':
        h.fx.spit(ev.x, ev.y, ev.toX, ev.toY);
        break;
      case 'bite': {
        h.fx.bite(ev.x, ev.y);
        h.juice.shake('mortar', now);
        const st = sim.byId(ev.structureId);
        if (st) h.alerts.raise(st.id, L.bitten(niceName(st.def)), 'normal', { x: st.i, y: st.j }, now);
        h.say(L.bitThrough(st?.def === 'wall' ? S.ev.wall : S.ev.it), 2600);
        break;
      }
      case 'dive':
        h.fx.ripple(ev.x, ev.y, 3);
        h.say(L.wentUnder(ev.name), 2400);
        break;
      case 'thread':
        h.fx.puff(ev.x, ev.y);
        break;
      case 'pulse':
        h.fx.ripple(ev.x, ev.y, 6);
        h.juice.shake('boss', now);
        h.alerts.raise(-1, L.towersDark(ev.towers), 'loud', { x: ev.x, y: ev.y }, now);
        h.say(L.pressurePulse(ev.towers), 3000);
        break;
      case 'lure':
        h.fx.ripple(ev.x, ev.y, 4);
        h.say(S.ev.lurePulsedSomething, 2400);
        break;
      case 'autoLure':
        h.showBanner(S.ev.rushHour, L.cameAnyway(ev.name));
        break;
      case 'riled':
        h.say(L.riled(ev.name), 3200);
        break;
      case 'waveCleared': {
        const total = (res: 'ore' | 'alloy' | 'ammo'): number =>
          Math.round(Math.max(0, ev.wages[res]) + ev.salvage[res]);
        const parts = [`+${total('ore')} ORE`, `+${total('alloy')} ALLOY`, `+${total('ammo')} AMMO`];
        const bonus = ev.bonus === 'double' ? S.ev.doubleReel : '';
        h.showBanner(S.ev.payday, `${parts.join(' · ')}${bonus}`);
        h.bankClear(ev);
        break;
      }
      case 'placed':
        // the rise, plus bubbles escaping the seabed (doc 17 §7)
        h.world.markPlaced(ev.structureId);
        h.fx.puff(ev.i + (ev.size - 1) / 2, ev.j + (ev.size - 1) / 2);
        // doc 08's depth-clear XP and doc 18 §7's run log both ask "which
        // towers did you deploy". The answer is whatever the SIM says was
        // placed, not whatever the hotbar remembers handing it.
        if (PLACEABLE[ev.def]?.kind === 'tower') h.ledger.deployed.add(ev.def);
        break;
      case 'upgraded': {
        h.world.markUpgraded(ev.structureId);
        const st = sim.byId(ev.structureId);
        if (st) {
          const c = blockCentre(st.i, st.j, st.size, st.size);
          h.fx.upgradeBurst(c.x, c.y - 10);
        }
        h.say(L.tierUpgraded(niceName(ev.def), ev.tier), 2200);
        break;
      }
      case 'structureDestroyed':
        h.alerts.raise(
          ev.structureId,
          L.destroyed(niceName(ev.def)),
          'loud',
          { x: ev.i, y: ev.j },
          now,
        );
        h.say(L.wasEaten(niceName(ev.def)), 2500);
        break;
      case 'brownout':
        if (ev.on) h.alerts.raise(-1, S.ev.brownoutAllSlower, 'normal', null, now);
        h.say(
          ev.on
            ? L.brownout(sim.power.demand, Math.round(sim.power.supply), Math.round(sim.power.efficiency * 100))
            : S.ev.powerRestoredLights,
          3500,
        );
        break;
      case 'victory':
        h.showBanner(`${sim.scenario.map.name}: CLEARED`, S.ev.depthYoursNext, true);
        h.finish('won');
        break;
      case 'defeat':
        h.showBanner(S.ev.colonyCrushed, S.ev.landlordDead, true);
        h.finish('lost');
        break;
      case 'enrage':
      case 'rejected':
        break;
    }
  }
}

/**
 * The name a player has actually seen. Never the content key: the bitmap font
 * has no underscore, so `subscription_canceller` renders as tofu — and even if
 * it did not, the hotbar never called it that.
 */
function niceName(def: string): string {
  return (placeableDef(def)?.name ?? def.replace(/_/g, ' ')).toUpperCase();
}

/** The Core taking damage is the loudest thing in the game (doc 17 §4/§7). */
export function watchCore(h: EventHost, lastCoreHp: number, now: number): number {
  const core = h.sim.byId(h.sim.state.coreId);
  if (!core) return lastCoreHp;
  // the first frame has nothing to compare against: a run that has just started
  // is not a Core under fire, and saying so would be the loudest possible lie
  if (!Number.isFinite(lastCoreHp)) return core.hp;
  if (core.hp < lastCoreHp - 0.5) {
    h.juice.shake('core', now);
    audio.play('hit_core');
    h.alerts.raise(core.id, S.ev.coreUnderFire, 'loud', { x: core.i, y: core.j }, now);
  }
  return core.hp;
}
